import {useMemo, useState} from 'react'
import {useNavigate} from 'react-router'

import {useDeimosStorageWithCompanyId} from '@product-web/api/deimos/storage'
import {PlanType, PlanUpgradeSource} from '@product-web/api/types/plan'
import {useFlags} from '@product-web/flags'
import {getIsCompanyOwner, getIsFdd, useUser} from '@product-web/shell/user'
import {useHasAWalletLoad} from '@product-web-features/funds-management/use-has-a-wallet-load'

import {usePricingPlan} from '~/entitlement/use-pricing-plan'
import {HowTrialWorksGuide} from '~/feature-ui/guides/how-trial-works/how-trial-works'
import {useCompanySubscription} from '~/paywall/lib/company-subscription'
import {ChoosePlanContainer} from '~/paywall/manage-plan/choose-plan-container'

import {TrialBanner} from './banner'
import {DEFAULT_TRIAL_LENGTH, getDatesDiffInDays} from './banner.helper'

export const TrialBannerFlagContainer = () => {
    const {trialBannerModals} = useFlags()
    const user = useUser()
    const isCompanyOwner = getIsCompanyOwner(user)

    if (!trialBannerModals || !isCompanyOwner) {
        return null
    }

    return <TrialBannerContainer />
}

const TrialBannerContainer = () => {
    const navigate = useNavigate()
    const user = useUser()
    const {data: subscription} = useCompanySubscription()
    const {hasAWalletLoad} = useHasAWalletLoad()
    const {pricingPlan} = usePricingPlan()
    const [isHowTrialWorksGuideOpen, setIsHowTrialWorksGuideOpen] = useState(false)
    const [isChoosePlansModalOpen, setIsChoosePlansModalOpen] = useState(false)
    // NOTE: included for backwards compatibility, can be removed when all trials which have this value expire
    // at end July 2023, also can remove `userDidSelectPremiumPlan` prop below, not required anymore
    // -- start --
    const {result: planSelectedDuringTrialStorageItem} = useDeimosStorageWithCompanyId<PlanType>(
        'planSelectedDuringTrial',
        user?.companyId,
    )
    const planSelectedDuringTrial = planSelectedDuringTrialStorageItem.data?.value
    // -- end --
    const trialLength = useMemo(
        () =>
            getDatesDiffInDays(
                subscription?.trialPeriod?.inclusiveStartDate,
                subscription?.trialPeriod?.exclusiveEndDate,
            ) || DEFAULT_TRIAL_LENGTH,
        [subscription?.trialPeriod],
    )
    const isFdd = getIsFdd(user)

    return (
        <div data-testid="trial-banner-container">
            <TrialBanner
                pricingPlan={pricingPlan}
                isBeforeTrial={!isFdd || (subscription?.isInTrialPeriod && !hasAWalletLoad)}
                isDuringTrial={subscription?.isInTrialPeriod && hasAWalletLoad}
                trialEndDate={subscription?.trialPeriod?.exclusiveEndDate}
                userDidSelectPremiumPlan={
                    planSelectedDuringTrial === PlanType.TEAM ||
                    planSelectedDuringTrial === PlanType.BUSINESS
                }
                handleCta={(modal) => {
                    if (modal === 'how-trial-works') {
                        setIsHowTrialWorksGuideOpen(true)
                    }
                    if (modal === 'choose-plans') {
                        setIsChoosePlansModalOpen(true)
                    }
                }}
            />
            {isHowTrialWorksGuideOpen && (
                <HowTrialWorksGuide
                    onDone={() => {
                        setIsHowTrialWorksGuideOpen(false)
                        navigate('/wallet')
                    }}
                    onDismiss={() => {
                        setIsHowTrialWorksGuideOpen(false)
                    }}
                    trialLength={trialLength}
                />
            )}
            {isChoosePlansModalOpen && (
                <ChoosePlanContainer
                    isOpen={isChoosePlansModalOpen}
                    onDismiss={() => setIsChoosePlansModalOpen(false)}
                    planUpgradeSource={PlanUpgradeSource.TRIAL_BANNER}
                    trackingOrigin="trial-banner"
                />
            )}
        </div>
    )
}
